import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getTrip } from '@/services/tripService'
import {
  listChecklistItems,
  createChecklistItem,
  setChecklistItemChecked,
  deleteChecklistItem,
} from '@/services/checklistService'
import {
  newChecklistItemSchema,
  CHECKLIST_CATEGORIES,
  type NewChecklistItemFormValues,
  type NewChecklistItemFormInput,
} from '@/utils/schemas'

export function ChecklistPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const itemsQuery = useQuery({
    queryKey: ['checklist-items', tripId],
    queryFn: () => listChecklistItems(tripId!),
    enabled: !!tripId,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewChecklistItemFormInput, unknown, NewChecklistItemFormValues>({
    resolver: zodResolver(newChecklistItemSchema),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['checklist-items', tripId] })

  const addItemMutation = useMutation({
    mutationFn: (values: NewChecklistItemFormValues) =>
      createChecklistItem({ tripId: tripId!, label: values.label, category: values.category }),
    onSuccess: async () => {
      await invalidate()
      reset()
    },
  })

  const toggleItemMutation = useMutation({
    mutationFn: ({ itemId, checked }: { itemId: string; checked: boolean }) =>
      setChecklistItemChecked(itemId, checked),
    onSuccess: invalidate,
  })

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => deleteChecklistItem(itemId),
    onSuccess: invalidate,
  })

  if (tripQuery.isLoading || itemsQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('checklist.loading')}</p>
  }

  const trip = tripQuery.data
  const items = itemsQuery.data ?? []

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('checklist.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('checklist.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        {items.length === 0 && <p className="text-sm text-mist">{t('checklist.empty')}</p>}

        {items.map((item) => (
          <div
            key={item.id}
            className="mb-2.5 flex items-center justify-between gap-3 rounded-md border border-line bg-panel-2 px-4 py-3"
          >
            <label className="flex flex-1 items-center gap-3">
              <input
                type="checkbox"
                checked={item.checked}
                onChange={(e) =>
                  toggleItemMutation.mutate({ itemId: item.id, checked: e.target.checked })
                }
                className="h-4 w-4"
              />
              <span
                className={
                  'font-sans text-[15px] ' +
                  (item.checked ? 'text-mist line-through' : 'text-ivory')
                }
              >
                {item.label}
              </span>
              {item.category && (
                <span className="font-mono text-[10px] tracking-wide text-brass-dim uppercase">
                  {t(`checklist.category.${item.category}`)}
                </span>
              )}
            </label>
            <button
              type="button"
              onClick={() => deleteItemMutation.mutate(item.id)}
              className="font-sans text-xs text-mist hover:text-brass-warm"
            >
              {t('checklist.deleteItem')}
            </button>
          </div>
        ))}

        <form
          onSubmit={handleSubmit((values) => addItemMutation.mutate(values))}
          className="mt-4 flex flex-col gap-3 rounded-md border border-line-strong bg-panel-2 p-4"
        >
          <input
            aria-label={t('checklist.label')}
            placeholder={t('checklist.label')}
            className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
            {...register('label')}
          />
          {errors.label && (
            <p role="alert" className="text-sm text-brass-warm">
              {t('checklist.labelRequired')}
            </p>
          )}
          <select
            aria-label={t('checklist.selectCategory')}
            className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
            {...register('category')}
          >
            <option value="">{t('checklist.selectCategory')}</option>
            {CHECKLIST_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {t(`checklist.category.${cat}`)}
              </option>
            ))}
          </select>
          {addItemMutation.isError && (
            <p role="alert" className="text-sm text-brass-warm">
              {t('checklist.saveError')}
            </p>
          )}
          <button
            type="submit"
            disabled={addItemMutation.isPending}
            className="rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
          >
            {t('checklist.addItem')}
          </button>
        </form>
      </div>
    </div>
  )
}
